import { Shield, Vault, Swords, PiggyBank } from "lucide-react";

/**
 * CommandCenterStrip — 4-tile stat row that anchors the top of the dashboard.
 *
 * Design intent: give the user a "command center" glance at their most
 * game-relevant numbers before scrolling. Every tile is bright, tight, and
 * tabular for at-a-glance scanning on mobile.
 */
export default function CommandCenterStrip({
  heroLevel = 1,
  levelLabel = "Recruit",
  vaultBalance = 0,
  billsDefeated = 0,
  billsDefeatedLabel = "This Month",
  monthSaved = 0,
  savedLabel = "Est. Savings",
}) {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-2.5"
      data-testid="command-center-strip"
    >
      <StatTile
        icon={<Shield className="w-6 h-6 text-[#22E07A]" strokeWidth={2.6} />}
        label="Hero Level"
        value={heroLevel}
        sub={levelLabel}
        subTone="text-[#22E07A]"
        testid="stat-hero-level"
      />
      <StatTile
        icon={<Vault className="w-6 h-6 text-[#F5D849]" strokeWidth={2.4} />}
        label="Hero Vault"
        value={`$${Number(vaultBalance).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`}
        valueClass="text-[#F5D849]"
        sub="Available"
        testid="stat-hero-vault"
      />
      <StatTile
        icon={<Swords className="w-6 h-6 text-[#22E07A]" strokeWidth={2.6} />}
        label="Bills Defeated"
        value={billsDefeated}
        sub={billsDefeatedLabel}
        testid="stat-bills-defeated"
      />
      <StatTile
        icon={<PiggyBank className="w-6 h-6 text-[#F5D849]" strokeWidth={2.4} />}
        label="This Month Saved"
        value={`$${Number(monthSaved).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`}
        valueClass="text-[#F5D849]"
        sub={savedLabel}
        testid="stat-month-saved"
      />
    </div>
  );
}

function StatTile({ icon, label, value, valueClass = "text-white", sub, subTone = "text-[#93B1A8]", testid }) {
  return (
    <div
      className="rounded-2xl bg-[#0B221D] border border-[#1B4238] p-3 flex flex-col items-center text-center min-w-0"
      data-testid={testid}
    >
      <div className="w-9 h-9 rounded-xl bg-[#11413B] flex items-center justify-center mb-1.5">
        {icon}
      </div>
      <p
        className="text-[9px] font-black uppercase tracking-[0.14em] text-[#93B1A8] leading-tight"
        style={{ fontFamily: "Manrope, sans-serif" }}
      >
        {label}
      </p>
      <p
        className={`mt-0.5 text-[17px] leading-tight font-black tabular-nums ${valueClass} truncate w-full`}
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        {value}
      </p>
      {sub && (
        <p className={`text-[9px] font-semibold ${subTone} mt-0.5 leading-tight`}>
          {sub}
        </p>
      )}
    </div>
  );
}
